import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

/**
 * Canonical shape for `lead.propertyDetails`. Use this when initializing
 * forms so a stale partial object never leaks `undefined` into Firestore.
 */
export const EMPTY_PROPERTY_DETAILS = Object.freeze({
  flashNote: '',
  gateCode: '',
  parkingNotes: '',
  entryInstructions: '',
  petOnSite: false,
  petNotes: '',
  alarmInfo: '',
  siteHazards: '',
});

/**
 * Controlled editor — UI only, no save logic. Used inside both:
 *   • NewLeadModal (within the larger form, parent handles save)
 *   • PropertyDetailsCard (handles its own save)
 *
 * Props:
 *   value:    object matching EMPTY_PROPERTY_DETAILS shape
 *   onChange: (next) => void  — full object replacement
 */
export default function PropertyDetailsEditor({ value, onChange }) {
  const v = { ...EMPTY_PROPERTY_DETAILS, ...(value || {}) };
  const set = (patch) => onChange({ ...v, ...patch });

  return (
    <div className="space-y-3" data-testid="property-details-editor">
      {/* Flash note (top — most-seen field) */}
      <div className="space-y-1">
        <Label htmlFor="pd-flash" className="text-xs font-semibold">
          Flash Note <span className="text-gray-400 font-normal">(short warning shown everywhere)</span>
        </Label>
        <Input
          id="pd-flash"
          value={v.flashNote}
          onChange={(e) => set({ flashNote: e.target.value.slice(0, 120) })}
          placeholder="e.g. Dog on property — call before knocking"
          maxLength={120}
          className="h-9"
          data-testid="pd-flash-input"
        />
        <p className="text-[10px] text-gray-400 text-right">
          {(v.flashNote || '').length}/120
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="pd-gate" className="text-xs font-semibold">Gate Code</Label>
          <Input
            id="pd-gate"
            value={v.gateCode}
            onChange={(e) => set({ gateCode: e.target.value })}
            placeholder="e.g. 4821 then #"
            className="h-9"
            data-testid="pd-gate-input"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pd-parking" className="text-xs font-semibold">Parking</Label>
          <Input
            id="pd-parking"
            value={v.parkingNotes}
            onChange={(e) => set({ parkingNotes: e.target.value })}
            placeholder="e.g. Street only — no driveway"
            className="h-9"
            data-testid="pd-parking-input"
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="pd-entry" className="text-xs font-semibold">Entry Instructions</Label>
        <Textarea
          id="pd-entry"
          rows={2}
          value={v.entryInstructions}
          onChange={(e) => set({ entryInstructions: e.target.value })}
          placeholder="e.g. Side door past the fence; lockbox 1142"
          className="text-sm"
          data-testid="pd-entry-input"
        />
      </div>

      <div className="rounded-md border border-gray-200 p-3 space-y-2 bg-gray-50/60">
        <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-gray-800">
          <input
            type="checkbox"
            checked={!!v.petOnSite}
            onChange={(e) => set({ petOnSite: e.target.checked })}
            className="h-4 w-4 accent-amber-500"
            data-testid="pd-petonsite-toggle"
          />
          Pet on site
        </label>
        {v.petOnSite && (
          <Input
            value={v.petNotes}
            onChange={(e) => set({ petNotes: e.target.value })}
            placeholder="e.g. Friendly golden retriever; keep gate latched"
            className="h-9"
            data-testid="pd-petnotes-input"
          />
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="pd-alarm" className="text-xs font-semibold">Alarm</Label>
          <Input
            id="pd-alarm"
            value={v.alarmInfo}
            onChange={(e) => set({ alarmInfo: e.target.value })}
            placeholder="e.g. Disarm before entry"
            className="h-9"
            data-testid="pd-alarm-input"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pd-hazards" className="text-xs font-semibold">Site Hazards</Label>
          <Input
            id="pd-hazards"
            value={v.siteHazards}
            onChange={(e) => set({ siteHazards: e.target.value })}
            placeholder="e.g. Loose deck board on back step"
            className="h-9"
            data-testid="pd-hazards-input"
          />
        </div>
      </div>
    </div>
  );
}
